import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Headset, Paperclip, Send } from 'lucide-react';
import { supportAPI } from '../../../../api/client';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { ErrorBanner, formatDate, formatTime, Modal } from '../../ui';
import { ACCEPT, FileChips, sizeLabel, useAttachments } from './TicketForm';

export const STATUS_TONE = { open: 'red', in_progress: 'blue', resolved: 'green', closed: 'grey' };
export const TicketStatus = ({ ticket, t }) => <span className={`hs-status ${STATUS_TONE[ticket.status]}`}>{t(ticket.status_label)}</span>;

function Files({ files }) {
  const get = async (f) => {
    const res = await supportAPI.download(f.id);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = f.filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  return files?.length ? (
    <div className="hs-files">{files.map((f) => (
      <button type="button" key={f.id} onClick={() => get(f).catch(() => {})}><Paperclip size={12} />{f.filename}<small>{sizeLabel(f.size)}</small><Download size={12} /></button>
    ))}</div>
  ) : null;
}

/** One ticket: what was asked, the conversation with TeleCalX Support, a reply box, and Close ticket. */
export default function TicketDialog({ id, onClose, onChanged }) {
  const { t } = useT();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const picker = useRef(null);
  const attach = useAttachments(t, setError);
  const load = useCallback(() => supportAPI.get(id).then((r) => setTicket(r.data)).catch((err) => setError(apiError(err))), [id]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const data = new FormData();
    data.append('body', reply.trim());
    attach.files.forEach((f) => data.append('files', f));
    try {
      const res = await supportAPI.reply(id, data);
      setTicket(res.data);
      setReply('');
      attach.clear();
      onChanged();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const close = async () => {
    setBusy(true);
    try { const res = await supportAPI.close(id); setTicket(res.data); onChanged(); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal wide title={ticket ? `${ticket.number} · ${ticket.subject}` : t('Loading…')} onClose={onClose}>
      <ErrorBanner message={error} />
      {ticket && (
        <div className="hs-ticket">
          <div className="hs-ticket-meta">
            <TicketStatus ticket={ticket} t={t} />
            <span>{t(ticket.category)}</span>
            <span>{t('Opened by {name}', { name: ticket.opened_by })} · {formatDate(ticket.created_at)}, {formatTime(ticket.created_at)}</span>
          </div>
          <div className="hs-thread">
            <div className="hs-msg mine"><b>{ticket.opened_by}</b><p>{ticket.description}</p><Files files={ticket.attachments} />
              <small>{formatDate(ticket.created_at)}, {formatTime(ticket.created_at)}</small></div>
            {ticket.messages.map((m) => (
              <div key={m.id} className={`hs-msg ${m.staff ? 'staff' : 'mine'}`}>
                <b>{m.staff ? <><Headset size={13} /> {t('TeleCalX Support')}</> : m.author}</b>
                {m.body && <p>{m.body}</p>}<Files files={m.attachments} />
                <small>{formatDate(m.created_at)}, {formatTime(m.created_at)}</small>
              </div>
            ))}
          </div>
          {ticket.status !== 'closed' ? (
            <form className="hs-reply" onSubmit={send}>
              <textarea rows={3} value={reply} maxLength={10000} placeholder={t('Write a reply…')} onChange={(e) => setReply(e.target.value)} />
              <FileChips files={attach.files} onRemove={attach.remove} />
              <div className="hs-reply-actions">
                <button type="button" className="secondary-action" onClick={() => picker.current?.click()}><Paperclip size={14} />{t('Attach')}</button>
                <input ref={picker} type="file" multiple hidden accept={ACCEPT} onChange={(e) => { attach.add([...e.target.files]); e.target.value = ''; }} />
                <button type="button" className="secondary-action" disabled={busy} onClick={close}>{t('Close ticket')}</button>
                <button className="primary-action" disabled={busy || (!reply.trim() && !attach.files.length)}><Send size={14} />{t('Send reply')}</button>
              </div>
              {ticket.status === 'resolved' && <small className="st-hint">{t('Replying reopens this ticket.')}</small>}
            </form>
          ) : <p className="hs-closed">{t('This ticket is closed. Open a new one if you still need help.')}</p>}
        </div>
      )}
    </Modal>
  );
}
